import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { NOTE_TYPES } from "@/lib/constants";
import { Plus, Trash, Note, Phone, EnvelopeSimple, ChatCircle, MapPin } from "@phosphor-icons/react";
import { devError } from "@/lib/log";

const ICONS = {
  note: Note,
  call: Phone,
  email: EnvelopeSimple,
  sms: ChatCircle,
  visit: MapPin,
};

function NoteRow({ item, onDelete }) {
  const Icon = ICONS[item.type] || Note;
  return (
    <li className="px-3 py-3 flex items-start gap-3" data-testid={`note-${item.id}`}>
      <div className="w-7 h-7 border border-canvas-line flex items-center justify-center shrink-0">
        <Icon size={13} weight="bold" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
              {item.type}
            </span>
            <span className="font-mono text-[10px] text-ink-subtle">
              {item.author_name} · {fmtDate(item.created_at)}
            </span>
          </div>
          <button
            onClick={() => onDelete(item.id)}
            data-testid={`note-delete-${item.id}`}
            className="p-1 hover:text-destructive"
            aria-label="Delete note"
          >
            <Trash size={11} weight="bold" />
          </button>
        </div>
        <p className="text-sm text-ink whitespace-pre-wrap break-words">{item.content}</p>
      </div>
    </li>
  );
}

export default function NotesTimeline({ customerId }) {
  const [notes, setNotes] = useState([]);
  const [type, setType] = useState("note");
  const [content, setContent] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/customers/${customerId}/notes`);
      setNotes(data);
    } catch (err) {
      devError("Failed to load notes:", err);
    }
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setAdding(true);
    try {
      await api.post(`/customers/${customerId}/notes`, { type, content });
      setContent("");
      toast.success("Activity logged");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/customers/${customerId}/notes/${id}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="bg-canvas-card border border-canvas-line" data-testid="notes-timeline">
      <div className="px-5 py-4 border-b border-canvas-line">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted mb-1">
          Activity Log
        </div>
        <h3 className="font-display text-lg font-semibold text-ink">
          Notes & touchpoints ({notes.length})
        </h3>
      </div>

      <form onSubmit={add} className="p-5 border-b border-canvas-line space-y-3" data-testid="note-form">
        <div className="flex gap-2 flex-wrap">
          {NOTE_TYPES.map((t) => {
            const Icon = ICONS[t.value];
            const active = type === t.value;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setType(t.value)}
                data-testid={`note-type-${t.value}`}
                className={`px-3 py-1.5 border text-[11px] font-mono uppercase tracking-wider flex items-center gap-1.5 ${
                  active
                    ? "border-ink bg-ink text-white"
                    : "border-canvas-line text-ink hover:border-ink"
                }`}
              >
                <Icon size={11} weight="bold" />
                {t.label}
              </button>
            );
          })}
        </div>
        <textarea
          data-testid="note-content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={2}
          placeholder="What happened?"
          className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
        />
        <button
          type="submit"
          disabled={adding || !content.trim()}
          data-testid="note-add-button"
          className="px-3 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand transition-colors disabled:opacity-60 flex items-center gap-1.5"
        >
          <Plus size={12} weight="bold" />
          {adding ? "Logging…" : "Add entry"}
        </button>
      </form>

      <ul className="divide-y divide-canvas-line max-h-[400px] overflow-y-auto">
        {notes.length === 0 && (
          <li className="px-5 py-8 font-mono text-xs text-ink-subtle text-center">
            No activity logged yet.
          </li>
        )}
        {notes.map((n) => (
          <NoteRow key={n.id} item={n} onDelete={remove} />
        ))}
      </ul>
    </div>
  );
}
